<script setup lang="ts">
import { computed, ref } from 'vue';
import { Check, UserPlus } from 'lucide-vue-next';
import { cl_errorMessage } from '@/api';
import { useLearnerStore } from '@/stores/learner';

const cl_learner = useLearnerStore();
const cl_profile = computed(() => cl_learner.cl_activeChild?.profile);
const cl_accent = ref<'EN_US' | 'EN_GB'>(cl_profile.value?.accent ?? 'EN_US');
const cl_dailyGoal = ref(cl_profile.value?.dailyGoalMinutes ?? 10);
const cl_parentNumbers = ref(cl_profile.value?.parentModeNumbers ?? false);
const cl_saving = ref(false);
const cl_saved = ref(false);
const cl_error = ref('');

async function cl_save() {
  cl_saving.value = true;
  cl_saved.value = false;
  cl_error.value = '';
  try {
    await cl_learner.cl_updateProfile({
      cl_accent: cl_accent.value,
      cl_dailyGoalMinutes: cl_dailyGoal.value,
      cl_parentModeNumbers: cl_parentNumbers.value,
    });
    cl_saved.value = true;
  } catch (cl_cause) {
    cl_error.value = cl_errorMessage(cl_cause);
  } finally {
    cl_saving.value = false;
  }
}
</script>

<template>
  <section class="cl-page cl-settings-page">
    <header class="cl-page-header">
      <p class="cl-eyebrow">Learning settings</p>
      <h1>{{ cl_learner.cl_activeChild?.name }} 的学习设置</h1>
    </header>
    <form class="cl-settings-form" @submit.prevent="cl_save">
      <section>
        <h2>主要口音</h2>
        <p>标准音频和发音评估会固定使用所选口音。</p>
        <div class="cl-choice-grid">
          <label :class="{ active: cl_accent === 'EN_US' }"><input v-model="cl_accent" type="radio" value="EN_US" /><strong>American English</strong><small>美式英语</small></label>
          <label :class="{ active: cl_accent === 'EN_GB' }"><input v-model="cl_accent" type="radio" value="EN_GB" /><strong>British English</strong><small>英式英语</small></label>
        </div>
      </section>
      <section>
        <h2>每日目标</h2>
        <p>首页会按照这个目标安排每日学习节奏。</p>
        <select v-model.number="cl_dailyGoal"><option :value="5">5 分钟</option><option :value="10">10 分钟</option><option :value="15">15 分钟</option><option :value="20">20 分钟</option><option :value="30">30 分钟</option></select>
      </section>
      <section class="cl-setting-row">
        <div><h2>家长数字模式</h2><p>练习结果中显示详细分数；关闭后儿童只看到鼓励与纠音建议。</p></div>
        <label class="cl-switch"><input v-model="cl_parentNumbers" type="checkbox" /><span /></label>
      </section>
      <p v-if="cl_error" class="cl-form-error">{{ cl_error }}</p>
      <p v-if="cl_saved" class="cl-save-success"><Check />设置已保存</p>
      <div class="cl-settings-actions"><button class="cl-primary-button" :disabled="cl_saving">{{ cl_saving ? '保存中...' : '保存设置' }}</button><RouterLink class="cl-icon-button" to="/onboarding"><UserPlus />添加孩子</RouterLink></div>
    </form>
  </section>
</template>
